"use client";

import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

// Count animation
function CountUp({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const numeric = parseInt(target.replace(/\D/g, ""));
    const increment = numeric / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= numeric) {
        setCount(numeric);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{count}{target.replace(/[\d]/g, "")}</span>;
}

export default function StatisticsSection() {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const statLabel     = isDark ? "text-gray-400"  : "text-gray-600";
  const statBoxBg     = isDark
    ? "bg-gradient-to-b from-[#0d1a0f] to-[#0a0a0a]"
    : "bg-gradient-to-b from-[#1a2e1a] to-[#0f1f0f]";
  const statBoxBorder = "border-green-400/20";
  const statBoxShadow = "shadow-[0_0_30px_rgba(74,222,128,0.08)]";

  return (
    <section
      className={`relative w-full overflow-hidden py-10 px-4 -mt-9 pb-18 transition-colors duration-300 ${
        isDark ? "bg-black text-white" : "bg-transparent text-gray-900"
      }`}
      style={isDark ? darkGridStyle : lightGridStyle}
    >
      <div className="relative mx-auto max-w-5xl space-y-10" style={{ zIndex: 1 }}>

        {/* ── Stats Section ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className={`rounded-2xl border ${statBoxBorder} ${statBoxBg} ${statBoxShadow} px-6 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center`}
        >
          {[
            { value: "10+",   label: "Projects Done",   delay: 0.1 },
            { value: "100k+", label: "Lines of Code",   delay: 0.25 },
            { value: "95%",   label: "Happy Customers", delay: 0.4 },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: stat.delay, duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col items-center gap-1"
            >
              <div className="text-4xl sm:text-5xl font-extrabold text-green-400 tracking-tight">
                <CountUp target={stat.value} />
              </div>
              <p className={`text-sm text-white font-sm ${statLabel}`}>{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* ── CTA Banner ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          viewport={{ once: true }}
          className="w-full rounded-2xl overflow-hidden"
        >
          <div className="w-full bg-gradient-to-r from-[#0b1c2c] via-[#0d2a3f] to-[#06121c] px-6 sm:px-10 py-10 sm:py-14 text-center">

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
              Ready to Build Something Great?
            </h2>

            <p className="text-sm sm:text-base text-gray-300 max-w-2xl mx-auto mb-6">
              Tell us about your project. We will get back to you within 24 hours.
            </p>

            {/* Single Green Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/contact-us")}
              className="px-8 py-3 rounded-full bg-green-400 text-black font-semibold hover:bg-green-300 transition-all shadow-[0_0_20px_rgba(74,222,128,0.4)]"
            >
              Get in Touch
            </motion.button>

          </div>
        </motion.div>

      </div>
    </section>
  );
}